import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import {
  BookOpen, Sparkles, Calculator, FlaskConical, PenLine, Languages, MessagesSquare, Globe, Heart, HandHeart,
  ArrowLeft, Star, Zap, Award, ChevronDown, ChevronRight,
  CheckCircle2, Circle, Clock, Trophy, Search, History,
} from "lucide-react";

const ICONS = { "book-open": BookOpen, sparkles: Sparkles, calculator: Calculator, "flask-conical": FlaskConical, "pen-line": PenLine, languages: Languages, "messages-square": MessagesSquare, globe: Globe, heart: Heart, "hand-heart": HandHeart };
const STATUS = {
  completed: { icon: CheckCircle2, tint: "#10B981", label: "Completed" },
  in_progress: { icon: Clock, tint: "#F59E0B", label: "In progress" },
  not_started: { icon: Circle, tint: "#94A3B8", label: "Not started" },
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function StatusPill({ status, testId }) {
  const s = STATUS[status] || STATUS.not_started;
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: `${s.tint}1F`, color: s.tint }} data-testid={testId}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

function SubjectCard({ subject, open, onToggle, filter }) {
  const Icon = ICONS[subject.icon] || BookOpen;
  const rows = useMemo(() => {
    const ql = (filter.q || "").trim().toLowerCase();
    return subject.lessons.filter((l) => {
      if (filter.status !== "all" && l.status !== filter.status) return false;
      if (!ql) return true;
      return (l.title || "").toLowerCase().includes(ql) || (l.grade || "").toLowerCase().includes(ql);
    });
  }, [subject.lessons, filter.q, filter.status]);

  return (
    <div className="rounded-3xl border border-[#E5DECF] bg-white overflow-hidden" data-testid={`fp-subject-${subject.id}`}>
      <button onClick={onToggle} className="flex w-full items-center gap-4 p-5 text-left hover:bg-[#FAF7F2]" data-testid={`fp-subject-toggle-${subject.id}`}>
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: subject.color }}><Icon size={24} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="font-heading text-lg font-bold text-[#1F2937]">{subject.name}</span>
            <span className="font-heading text-sm font-bold" style={{ color: subject.color }}>{subject.progress_percentage}%</span>
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-[#F2ECE1]">
            <div className="h-full rounded-full transition-all" style={{ width: `${subject.progress_percentage}%`, background: subject.color }} />
          </div>
          <div className="mt-1.5 font-body text-xs text-[#6B7280]">{subject.lessons_completed}/{subject.lessons_total} lessons · {subject.activities_completed}/{subject.activities_total} activities</div>
        </div>
        {open ? <ChevronDown size={20} className="text-[#6B7280]" /> : <ChevronRight size={20} className="text-[#6B7280]" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="rows" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} className="border-t border-[#E5DECF] bg-[#FAF7F2]">
            {rows.length === 0 ? (
              <div className="px-5 py-6 text-center font-body text-sm text-[#6B7280]" data-testid={`fp-lessons-empty-${subject.id}`}>No lessons match your filter.</div>
            ) : (
              <ul className="divide-y divide-[#E5DECF]" data-testid={`fp-lessons-${subject.id}`}>
                {rows.map((l) => (
                  <li key={l.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`fp-lesson-${l.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-bold text-[#1F2937]">{l.title}</span>
                        {l.mastered && <Trophy size={14} className="text-[#F59E0B]" title="Mastered" />}
                      </div>
                      <div className="font-body text-xs text-[#6B7280]">{l.grade || "—"} · {l.activities_completed}/{l.activities_total} activities · last {fmtWhen(l.last_attempt_at)}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-sm font-bold text-[#1F2937] min-w-[3ch] text-right" data-testid={`fp-score-${l.id}`}>{l.best_score || 0}%</div>
                      <StatusPill status={l.status} testId={`fp-status-${l.id}`} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FamilyProgress() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [student, setStudent] = useState(null);
  const [openIds, setOpenIds] = useState({});
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    api.familyProgress(studentId).then(setData).catch(() => setData(false));
    api.report(studentId).then((r) => setStudent(r.student)).catch(() => {});
  }, [studentId]);

  if (data === false) return <div className="p-16 text-center font-heading text-xl text-[#6B7280]">No access to this student.</div>;
  if (!data) return <div className="p-16 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading…</div>;

  const toggle = (id) => setOpenIds((o) => ({ ...o, [id]: !o[id] }));
  const filter = { q, status };
  const totalCompleted = data.subjects.reduce((s, x) => s + x.lessons_completed, 0);
  const totalLessons = data.subjects.reduce((s, x) => s + x.lessons_total, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <button onClick={() => navigate(`/students/${studentId}`)} data-testid="fp-back" className="mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-[#6B7280] hover:bg-[#F2ECE1]">
        <ArrowLeft size={16} /> Student Report
      </button>

      <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Lesson Progress Tracker</div>
      <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1F2937]">{student?.name || "Student"}</h1>

      <div className="mt-5 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 rounded-2xl border border-[#E5DECF] bg-white px-4 py-3">
          <Zap size={18} className="text-[#F59E0B]" /><span className="font-heading text-lg font-bold text-[#1F2937]" data-testid="fp-points">{data.points}</span>
          <span className="font-body text-sm text-[#6B7280]">points</span>
        </div>
        <div className="flex items-center gap-2 rounded-2xl border border-[#E5DECF] bg-white px-4 py-3">
          <CheckCircle2 size={18} className="text-[#10B981]" /><span className="font-heading text-lg font-bold text-[#1F2937]" data-testid="fp-lessons-done">{totalCompleted}</span>
          <span className="font-body text-sm text-[#6B7280]">/ {totalLessons} lessons done</span>
        </div>
        <div className="flex items-center gap-2 rounded-2xl border border-[#E5DECF] bg-white px-4 py-3">
          <Award size={18} className="text-[#7A3FF2]" /><span className="font-heading text-lg font-bold text-[#1F2937]" data-testid="fp-stickers">{data.stickers?.length || 0}</span>
          <span className="font-body text-sm text-[#6B7280]">stickers</span>
        </div>
      </div>

      {data.recent && data.recent.length > 0 && (
        <div className="mt-8 rounded-3xl border border-[#E5DECF] bg-white p-5" data-testid="fp-recent">
          <div className="flex items-center gap-2"><History size={16} className="text-[#0D9488]" /><h2 className="font-heading text-lg font-bold text-[#1F2937]">Recent activity</h2></div>
          <ul className="mt-3 divide-y divide-[#F2ECE1]">
            {data.recent.slice(0, 8).map((r) => {
              const Icon = ICONS[r.subject_icon] || BookOpen;
              return (
                <li key={`${r.lesson_id}-${r.last_attempt_at}`} className="flex flex-wrap items-center gap-3 py-2.5" data-testid={`fp-recent-${r.lesson_id}`}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl text-white" style={{ background: r.subject_color }}><Icon size={14} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-sm font-bold text-[#1F2937] truncate">{r.lesson_title}</div>
                    <div className="font-body text-xs text-[#6B7280]">{r.subject_name} · {r.grade} · {fmtWhen(r.last_attempt_at)}</div>
                  </div>
                  <div className="font-mono text-sm font-bold text-[#1F2937]">{r.best_score || 0}%</div>
                  <StatusPill status={r.status} />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="fp-search"
            placeholder="Search a lesson or grade…"
            className="w-72 rounded-full border-2 border-[#E5DECF] bg-white pl-8 pr-3 py-1.5 text-sm font-semibold text-[#1F2937] focus:border-[#0D9488] outline-none" />
        </div>
        <div className="inline-flex rounded-full border border-[#E5DECF] bg-[#FAF7F2] p-1" data-testid="fp-status-filter">
          {[["all", "All"], ["completed", "Completed"], ["in_progress", "In progress"], ["not_started", "Not started"]].map(([id, label]) => (
            <button key={id} onClick={() => setStatus(id)} data-testid={`fp-status-${id}`}
              className={`rounded-full px-3 py-1 text-xs font-bold transition-all ${status === id ? "bg-white shadow text-[#1F2937]" : "text-[#6B7280] hover:text-[#1F2937]"}`}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => setOpenIds(Object.fromEntries(data.subjects.map((s) => [s.id, true])))} data-testid="fp-expand-all"
          className="ml-auto rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1.5 text-xs font-bold hover:border-[#0D9488]">Expand all</button>
        <button onClick={() => setOpenIds({})} data-testid="fp-collapse-all"
          className="rounded-full border-2 border-[#E5DECF] bg-white px-3 py-1.5 text-xs font-bold hover:border-[#0D9488]">Collapse all</button>
      </div>

      <div className="mt-4 space-y-3">
        {data.subjects.map((s) => (
          <SubjectCard key={s.id} subject={s} open={!!openIds[s.id]} onToggle={() => toggle(s.id)} filter={filter} />
        ))}
      </div>

      {data.stickers?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">Stickers earned</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            {data.stickers.map((b) => (
              <div key={b.id} className="flex items-center gap-2 rounded-2xl border-2 px-3 py-2" style={{ borderColor: b.color, background: `${b.color}12` }}>
                <Star size={16} style={{ color: b.color }} /><span className="font-heading text-sm font-bold text-[#1F2937]">{b.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
